import { useCallback, useEffect, useState } from 'react';
import './App.css';

const API_BASE_URL = 'https://apibingwa.bingwaturbo.co.ke';

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return '—';
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
}

function isUsefulChangelog(text) {
  if (!text) return false;
  const value = String(text).trim();
  if (value.length < 4) return false;
  return !/^(n\/?a|none|nb|test|todo|-)$/i.test(value);
}

function BrandMark() {
  return (
    <a className="brand" href="#top" aria-label="Bingwa Turbo home">
      <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" className="brand-logo" />
      <span className="brand-copy">
        <span className="brand-name">Bingwa Turbo</span>
        <span className="brand-byline">A Ziz company product</span>
      </span>
    </a>
  );
}

function Topbar() {
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <BrandMark />
        <nav className="top-nav" aria-label="Primary">
          <a href="#product">Product</a>
          <a className="nav-download" href="#download">Download</a>
          <a href="#install">Install guide</a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" className="footer-logo" />
          <div>
            <p className="footer-name">Bingwa Turbo</p>
            <p className="footer-tag">Automatic data, SMS &amp; minutes sales</p>
          </div>
        </div>
        <div className="powered-by">
          <span className="powered-label">Powered by</span>
          <span className="ziz-mark" aria-label="Ziz">
            <span className="ziz-glyph" aria-hidden="true">Z</span>
            Ziz
          </span>
        </div>
        <p className="copyright">
          © {new Date().getFullYear()} Ziz. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

function App() {
  const [appInfo, setAppInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadError, setDownloadError] = useState(null);

  const fetchVersion = useCallback(() => {
    fetch(`${API_BASE_URL}/api/app/version`)
      .then((res) => {
        if (!res.ok) throw new Error('Unable to reach the release server');
        return res.json();
      })
      .then((data) => {
        if (data.success && data.data) {
          setAppInfo(data.data);
          setErrorMessage(null);
        } else {
          setAppInfo(null);
          setErrorMessage(data.message || 'No active version is available right now.');
        }
      })
      .catch(() => {
        setAppInfo(null);
        setErrorMessage('We could not load the latest release. Please try again.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchVersion();
    const interval = setInterval(fetchVersion, 30000);
    return () => clearInterval(interval);
  }, [fetchVersion]);

  const handleDownload = async () => {
    if (!appInfo?.apkUrl || downloading) return;

    setDownloading(true);
    setProgress(0);
    setDownloadError(null);

    try {
      const downloadUrl = `${API_BASE_URL}${appInfo.apkUrl}`;
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');

      const total =
        Number(appInfo.fileSize) ||
        Number(response.headers.get('content-length')) ||
        0;

      if (response.body && response.body.getReader) {
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          if (total) setProgress(Math.min(100, Math.round((received / total) * 100)));
        }

        const blob = new Blob(chunks, {
          type: 'application/vnd.android.package-archive',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bingwa-turbo-${appInfo.version}.apk`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bingwa-turbo-${appInfo.version}.apk`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      setProgress(100);
    } catch (err) {
      console.error(err);
      window.location.href = `${API_BASE_URL}${appInfo.apkUrl}`;
    } finally {
      setDownloading(false);
    }
  };

  const changelogLines = isUsefulChangelog(appInfo?.changelog)
    ? String(appInfo.changelog)
        .split(/\r?\n/)
        .map((line) => line.replace(/^[-•*]\s*/, '').trim())
        .filter(Boolean)
    : [];

  return (
    <div className="page" id="top">
      <Topbar />

      <main>
        <section className="hero">
          <div className="hero-inner">
            <div className="hero-copy">
              <p className="eyebrow">Official Android app by Ziz</p>
              <h1>
                Bingwa Turbo
                <span>Sell data, SMS and minutes automatically.</span>
              </h1>
              <p className="lede">
                Fulfill data, SMS, and minutes orders automatically.
              </p>
              <ul className="trust-row">
                <li>Data bundles</li>
                <li>SMS packs</li>
                <li>Minutes &amp; airtime</li>
                <li>Powered by Ziz</li>
              </ul>
            </div>

            <div className="release-panel" id="download">
              {loading && (
                <div className="panel-state" role="status">
                  <div className="spinner" aria-hidden="true" />
                  <p>Checking the latest release…</p>
                </div>
              )}

              {!loading && errorMessage && (
                <div className="panel-state">
                  <p className="state-title">Release unavailable</p>
                  <p className="state-copy">{errorMessage}</p>
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setLoading(true);
                    fetchVersion();
                  }}>
                    Try again
                  </button>
                </div>
              )}

              {!loading && appInfo && (
                <>
                  <div className="panel-head">
                    <img
                      src={`${process.env.PUBLIC_URL}/logo.png`}
                      alt="Bingwa Turbo"
                      className="app-icon"
                    />
                    <div>
                      <p className="panel-kicker">Latest release</p>
                      <h2>Version {appInfo.version}</h2>
                    </div>
                  </div>

                  <dl className="meta-grid">
                    <div>
                      <dt>Version</dt>
                      <dd>{appInfo.version}</dd>
                    </div>
                    <div>
                      <dt>Build</dt>
                      <dd>{appInfo.buildNumber}</dd>
                    </div>
                    <div>
                      <dt>Size</dt>
                      <dd>{formatFileSize(appInfo.fileSize)}</dd>
                    </div>
                    <div>
                      <dt>Platform</dt>
                      <dd>Android</dd>
                    </div>
                  </dl>

                  {changelogLines.length > 0 && (
                    <div className="changelog">
                      <h3>What’s new</h3>
                      <ul>
                        {changelogLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    {downloading
                      ? progress > 0
                        ? `Downloading ${progress}%`
                        : 'Preparing download…'
                      : `Download APK · v${appInfo.version}`}
                  </button>

                  {downloading && (
                    <div
                      className="progress"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={progress}
                    >
                      <span style={{ width: `${progress}%` }} />
                    </div>
                  )}

                  {downloadError && <p className="inline-error">{downloadError}</p>}

                  <p className="fine-print">
                    Allow this browser to install apps, then open the file.{' '}
                    <a
                      href={`${API_BASE_URL}${appInfo.apkUrl}`}
                      download={`bingwa-turbo-${appInfo.version}.apk`}
                    >
                      Direct link
                    </a>
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="product" id="product">
          <div className="product-inner">
            <p className="section-kicker">What it does</p>
            <h2>Automatic sales for your airtime business</h2>
            <ul className="feature-grid">
              <li>
                <span className="feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <rect x="4" y="14" width="3.2" height="6" rx="0.8" />
                    <rect x="10.4" y="10" width="3.2" height="10" rx="0.8" />
                    <rect x="16.8" y="6" width="3.2" height="14" rx="0.8" />
                  </svg>
                </span>
                <h3>Data</h3>
                <p>Sell data bundles as soon as an order comes in.</p>
              </li>
              <li>
                <span className="feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 6.5h14v9.5H8.5L5 19V6.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <path d="M8.5 10h7M8.5 13h4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <h3>SMS</h3>
                <p>Fulfill SMS packs without dialing menus by hand.</p>
              </li>
              <li>
                <span className="feature-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="7" y="3.5" width="10" height="17" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M10 17.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <h3>Minutes</h3>
                <p>Send minutes and airtime while you serve the next customer.</p>
              </li>
            </ul>
          </div>
        </section>

        <section className="install" id="install">
          <div className="install-inner">
            <p className="section-kicker">Getting started</p>
            <h2>Install in three steps</h2>
            <ol className="steps">
              <li>
                <span className="step-num">01</span>
                <h3>Download the APK</h3>
                <p>Use the official button. The file is served by Ziz.</p>
              </li>
              <li>
                <span className="step-num">02</span>
                <h3>Allow this source</h3>
                <p>Allow your browser to install unknown apps.</p>
              </li>
              <li>
                <span className="step-num">03</span>
                <h3>Open and install</h3>
                <p>Open the file and finish the on-screen installer.</p>
              </li>
            </ol>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default App;
